import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import PageBlock from "../../components/PageBlock";
import {Button, CardBody} from "@material-tailwind/react";
import {User} from "../../types/User";
import {useUserContext} from "../../types/contexts/UserContext";
import FormSkeleton from "../../components/Skeletons/FormSkeleton";
import {useToastContext} from "../../types/contexts/ToastContext";
import {useErrorHandler} from "react-error-boundary";
import {clear, validateData} from "../../reducers/validationReducer";
import {useDispatch} from "react-redux";
import {useSystemContext} from "../../types/contexts/SystemContext";
import OutlineInput from "../../components/OutlineInput";
import Loader from "../../assets/icons/Loader";
import {InputType} from "../../types/InputType";
import {API} from "../../types/API";
import {AxiosResponse} from "axios";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {ErrorHandler} from "../../hooks/errorHandler";
import {useTranslation} from "react-i18next";
import Card from "../../components/Card";
import {setTitle} from "../../hooks/setTitle";
import {APILink} from "../../types/APILink";
import {setSidebarTitle} from "../../reducers/sidebar";

const ChangePassword = () => {
    const {t} = useTranslation();
    const userContext = useUserContext();
    const toast = useToastContext();
    const system = useSystemContext();
    const handleError = useErrorHandler();

    const [user, setUser] = useState<User>();
    const [isReady, setIsReady] = useState<boolean>(false);


    const [validated, setValidated] = useState<boolean>(false);
    const [isInProgress, setInProgress] = useState<boolean>(false);

    const [password, setPassword] = useState<string>('');
    const [new_password, setNewPassword] = useState<string>('');
    const [confirm_password, setConfirmPassword] = useState<string>('');
    const [code, setCode] = useState<string>('');

    const [showPasswords, setShowPasswords] = useState<{ old: boolean; new: boolean; confirm: boolean }>({
        old: false,
        new: false,
        confirm: false
    });

    const dispatch = useDispatch();

    const sendRequest = useCallback(() => {
        if (!userContext || !system || !system.system)
            return;

        setUser(userContext.user);
        setIsReady(true);
    }, [user, userContext, system, setUser, setIsReady]);

    const onPasswordChanged = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setPassword(e.currentTarget.value)
        }
    }, [setValidated, setPassword]);

    const onNewPasswordChanged = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setNewPassword(e.currentTarget.value)
        }
    }, [setValidated, setNewPassword]);

    const onConfirmPasswordChanged = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setConfirmPassword(e.currentTarget.value)
        }
    }, [setValidated, setConfirmPassword]);

    const onCodeChanged = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setCode(e.currentTarget.value)
        }
    }, [setValidated, setCode]);

    const callback = useCallback((b: boolean) => {
        if (!user)
            return;

        setValidated(true);
        let request = {password, new_password};
        if (user.two_fa) {
            request = {...request, ['code' as keyof typeof request]: code};
        } else request = {...request, ['confirm_password' as keyof typeof request]: confirm_password}

        if (b) {
            API.updatePassword(request).then((response: AxiosResponse) => {
                setInProgress(false);
                setUser(response.data.data as User);
                setValidated(false);
                dispatch(clear());
                if (response.data.status) {
                    toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                                   className="mr-4"/>{t('admin.change_password.success')}</>, "bg-teal-500")
                    setTimeout(function () {
                        window.location.replace('/app/admin/');
                    }, 5000);
                }
            }).catch((e) => {
                setInProgress(false);
                ErrorHandler(e, `post${APILink.UPDATE_PASSWORD}`, toast, handleError)
            });
        } else setInProgress(false)

    }, [user, code, password, new_password, confirm_password, toast, handleError]);

    const submitPasswordForm = useCallback(() => {
        if (!user)
            return;
        setInProgress(true);
        const inputs = [
            {key: 'password', label: t('admin.change_password.password'), type: InputType.text, regex: '^.*\\S.*$'},
            {
                key: 'new_password',
                label: t('admin.change_password.new_password'),
                type: InputType.text,
                regex: '^.*\\S.*$'
            }
        ];
        if (!user.two_fa) {
            inputs.push({
                key: 'confirm_password',
                label: t('admin.change_password.confirm_password'),
                type: InputType.text,
                regex: '^.*\\S.*$'
            });
        } else inputs.push({
            key: 'code',
            label: t('admin.change_password.code'),
            type: InputType.number,
            regex: '^\\d{6}\\b$'
        })
        dispatch(validateData({
            payload: {
                inputs: inputs,
                introducedData: {password, new_password, confirm_password, code},
                callback: callback
            }
        }));
    }, [user, password, new_password, confirm_password, code]);

    const onShowPassword = useCallback((field: 'old' | 'new' | 'confirm') => {
        setShowPasswords({...showPasswords, [field as keyof typeof showPasswords]: !showPasswords[field]});
    }, [showPasswords]);


    useEffect(() => {
        setTitle(t('admin.change_password.title'))
        dispatch(setSidebarTitle(t('admin.change_password.title')))
        sendRequest();
        dispatch(clear());
    }, [sendRequest, dispatch]);


    const form = useMemo(() => {
        if (!user)
            return <></>

        return <>
            <OutlineInput
                name={t('admin.change_password.password')}
                value={password}
                fieldName='password'
                validated={validated}
                type={showPasswords.old ? 'text' : 'password'}
                onChange={onPasswordChanged}
                icon={showPasswords.old ? <FontAwesomeIcon onClick={() => onShowPassword('old')} icon={solid('eye-slash')} className="cursor-pointer w-5 h-5"/>
                    : <FontAwesomeIcon onClick={() => onShowPassword('old')} icon={solid('eye')} className="cursor-pointer w-5 h-5"/>
                }
            />
            <OutlineInput className="mt-4"
                          name={t('admin.change_password.new_password')}
                          value={new_password}
                          fieldName='new_password'
                          validated={validated}
                          type={showPasswords.new ? 'text' : 'password'}
                          onChange={onNewPasswordChanged}
                          icon={showPasswords.new ? <FontAwesomeIcon onClick={() => onShowPassword('new')} icon={solid('eye-slash')} className="cursor-pointer w-5 h-5"/>
                              : <FontAwesomeIcon onClick={() => onShowPassword('new')} icon={solid('eye')} className="cursor-pointer w-5 h-5"/>
                          }
            />
            {!user.two_fa ?
                <OutlineInput className="mt-4" name={t('admin.change_password.confirm_password')}
                              value={confirm_password}
                              fieldName='confirm_password'
                              validated={validated}
                              type={showPasswords.confirm ? 'text' : 'password'}
                              onChange={onConfirmPasswordChanged}
                              icon={showPasswords.confirm ? <FontAwesomeIcon onClick={() => onShowPassword('confirm')} icon={solid('eye-slash')} className="cursor-pointer w-5 h-5"/>
                                  : <FontAwesomeIcon onClick={() => onShowPassword('confirm')} icon={solid('eye')} className="cursor-pointer w-5 h-5"/>
                              }/> :
                <OutlineInput className="mt-4" name={t('admin.change_password.code')} value={code} fieldName='code'
                              validated={validated} type='number' onChange={onCodeChanged}/>}

            <div className="mt-8">
                <Button className="bg-teal-500" disabled={isInProgress}
                        onClick={submitPasswordForm}>{isInProgress ?
                    <Loader/> : t('Submit')}</Button>
            </div>
        </>
    }, [showPasswords, user, password, new_password, confirm_password, code, isInProgress, validated, submitPasswordForm, onPasswordChanged, onNewPasswordChanged, onCodeChanged]);

    return <PageBlock>
        {!isReady ? <FormSkeleton/> : <>
            <Card className="mt-6 bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                <CardBody className="overflow-auto px-0">
                    {form}
                </CardBody>
            </Card>
        </>}
    </PageBlock>
}
export default memo(ChangePassword);
